import React, { useEffect, useState } from "react";
import styles from "./Form.module.css";
import { SEO_PAGES, loadSeoSettings, saveSeoSettings, normalizePath } from "../utils/seo";

export default function SeoManagement() {
  const [selectedPage, setSelectedPage] = useState("/");
  const [pageSettings, setPageSettings] = useState({});
  const [form, setForm] = useState({
    page_title: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
  });
  const [saved, setSaved] = useState(false);
  const knownPagePaths = new Set(SEO_PAGES.map((page) => page.path));
  const shouldShowCustomPageOption = selectedPage && !knownPagePaths.has(selectedPage);

  useEffect(() => {
    const settings = loadSeoSettings();
    setSelectedPage(settings.selectedPage || "/");
    setPageSettings(settings.pages || {});
  }, []);

  useEffect(() => {
    if (!pageSettings[selectedPage]) {
      setForm({
        page_title: "",
        meta_title: "",
        meta_description: "",
        meta_keywords: "",
      });
      return;
    }

    setForm({
      page_title: pageSettings[selectedPage].page_title || "",
      meta_title: pageSettings[selectedPage].meta_title || "",
      meta_description: pageSettings[selectedPage].meta_description || "",
      meta_keywords: pageSettings[selectedPage].meta_keywords || "",
    });
  }, [selectedPage, pageSettings]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handlePageChange = (event) => {
    setSelectedPage(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const normalizedPath = normalizePath(selectedPage);
    const updatedPages = {
      ...pageSettings,
      [normalizedPath]: {
        page_title: form.page_title,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        meta_keywords: form.meta_keywords,
      },
    };

    const settings = {
      selectedPage: normalizedPath,
      pages: updatedPages,
    };

    try {
      saveSeoSettings(settings);
      setPageSettings(updatedPages);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error("Failed to save SEO settings", e);
    }
  };

  return (
    <div className={styles.card} style={{ padding: "24px" }}>
      <div className={styles.title}>SEO Management</div>
      <p className={styles.hint} style={{ marginBottom: "18px" }}>
        Manage page-specific metadata for SEO and social previews.
      </p>
      <form className={styles.grid} onSubmit={handleSubmit}>
        <div className={styles.row}>
          <label className={styles.label} htmlFor="page_selector">
            Select page
          </label>
          <select
            id="page_selector"
            className={styles.input}
            value={selectedPage}
            onChange={handlePageChange}
          >
            {SEO_PAGES.map((page) => (
              <option key={page.path} value={page.path}>
                {page.label}
              </option>
            ))}
            {shouldShowCustomPageOption && (
              <option value={selectedPage}>{selectedPage}</option>
            )}
          </select>
        </div>

        <div className={styles.row}>
          <label className={styles.label} htmlFor="page_path">
            Page path
          </label>
          <input
            id="page_path"
            name="page_path"
            className={styles.input}
            value={selectedPage}
            onChange={(event) => setSelectedPage(event.target.value)}
            placeholder="Enter a page path, e.g. /blog, /photo-gallery"
          />
        </div>

        <div className={styles.row}>
          <label className={styles.label} htmlFor="page_title">
            Page Title
          </label>
          <input
            id="page_title"
            name="page_title"
            className={styles.input}
            value={form.page_title}
            onChange={updateField}
            placeholder="Homepage page title"
          />
        </div>

        <div className={styles.row}>
          <label className={styles.label} htmlFor="meta_title">
            Meta Title
          </label>
          <input
            id="meta_title"
            name="meta_title"
            className={styles.input}
            value={form.meta_title}
            onChange={updateField}
            placeholder="SEO title for the site"
          />
        </div>

        <div className={styles.row}>
          <label className={styles.label} htmlFor="meta_description">
            Meta Description
          </label>
          <textarea
            id="meta_description"
            name="meta_description"
            className={styles.textarea}
            value={form.meta_description}
            onChange={updateField}
            placeholder="SEO description for the site"
          />
        </div>

        <div className={styles.row}>
          <label className={styles.label} htmlFor="meta_keywords">
            Meta Keywords
          </label>
          <input
            id="meta_keywords"
            name="meta_keywords"
            className={styles.input}
            value={form.meta_keywords}
            onChange={updateField}
            placeholder="keyword1, keyword2, keyword3"
          />
        </div>

        <div className={styles.actions} style={{ justifyContent: "flex-start" }}>
          <button type="submit" className={styles.button}>
            Save SEO Settings
          </button>
          {saved && <span style={{ marginLeft: 12, color: "#16a34a" }}>Saved!</span>}
        </div>
      </form>
    </div>
  );
}
